<script>
import { capitalizeFirstLetter } from '@/components/utils/globalFunction'

export default {
  name: 'CustomDateInput',
  props: {
    modelValue: {
      required: true
    },
    maxDate: {
      required: false,
      default: null
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      localModel: this.modelValue,
      manualInputValue: this.modelValue,
      pickerObserver: null,
      pickerContentObserver: null,
      isEdgeProcessing: false
    }
  },
  mounted() {
    this.initializePickerObserver()
  },
  beforeUnmount() {
    if (this.pickerObserver) {
      this.pickerObserver.disconnect()
    }
    if (this.pickerContentObserver) {
      this.pickerContentObserver.disconnect()
    }
  },
  methods: {
    initializePickerObserver() {
      this.pickerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const picker = node.querySelector?.('.v-date-picker')

              if (picker) {
                setTimeout(() => {
                  this.customizePickerButtons()
                  this.observePickerContent(picker)
                }, 350)
              }
            }
          })

          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const picker = node.querySelector?.('.v-date-picker')

              if (picker && this.pickerContentObserver) {
                this.pickerContentObserver.disconnect()
                this.pickerContentObserver = null
              }
            }
          })
        })
      })

      this.pickerObserver.observe(document.body, {
        childList: true,
        subtree: true
      })
    },
    observePickerContent(picker) {
      this.pickerContentObserver = new MutationObserver((mutations) => {
        let shouldUpdate = false

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const target = mutation.target
            if (target.closest && target.closest('.v-date-picker-controls')) {
              shouldUpdate = true
            }
          }
        })

        if (shouldUpdate && !this.isEdgeProcessing) {
          this.isEdgeProcessing = true
          setTimeout(() => {
            this.customizePickerButtons()
            this.isEdgeProcessing = false
          }, 100)
        }
      })

      const pickerControls = picker.querySelector('.v-date-picker-controls')
      if (pickerControls) {
        this.pickerContentObserver.observe(pickerControls, {
          childList: true,
          subtree: true,
          characterData: true
        })
      }
    },
    customizePickerButtons() {
      const pickerButtons = document.querySelectorAll('.v-date-picker-controls button')

      if (pickerButtons.length >= 2) {
        let monthButton = pickerButtons[0]
        let yearButton = pickerButtons[1]

        yearButton.className = monthButton.className

        if (monthButton && yearButton) {
          const monthContent = monthButton.querySelector('.v-btn__content')
          const yearContent = yearButton.querySelector('.v-btn__content')

          if (!monthContent || !yearContent) return

          const originalText = monthContent.textContent.split(' ')
          const currentMonth = originalText[0]
          const currentYear = originalText[1]

          const oldMonthClone = monthButton.querySelector('.custom-month-clone')
          if (oldMonthClone) {
            oldMonthClone.remove()
          }

          const oldArrow = yearContent.querySelector('.custom-arrow')
          if (oldArrow) {
            oldArrow.remove()
          }

          const monthClone = monthContent.cloneNode(true)
          monthClone.classList.add('custom-month-clone')
          monthClone.innerHTML = `${capitalizeFirstLetter(currentMonth)}`
          monthClone.style.display = 'initial'

          monthContent.style.display = 'none'

          monthButton.appendChild(monthClone)

          yearContent.textContent = currentYear

          const createArrowIcon = () => {
            const icon = document.createElement('i')
            icon.className = 'mdi mdi-chevron-down v-icon notranslate v-theme--theme v-icon--size-default custom-arrow'
            icon.setAttribute('aria-hidden', 'true')
            icon.style.marginLeft = '4px'
            return icon
          }

          monthClone.appendChild(createArrowIcon())
          yearContent.appendChild(createArrowIcon())

          monthButton.style.display = 'none'
          yearButton.style.display = 'none'

          requestAnimationFrame(() => {
            monthButton.style.display = ''
            yearButton.style.display = ''
          })
        }
      }
    },
    emitValue(value) {
      this.localModel = value
      this.$emit('update:modelValue', value)
    },
    handleClearValue() {
      this.emitValue(null)
      this.manualInputValue = null
    },
    handleDateUpdate(value) {
      this.emitValue(value)
      this.manualInputValue = value
    },
    handleManualInput(event) {
      const inputValue = event.target.value
      this.manualInputValue = inputValue

      const cleanInput = inputValue.replace(/\D/g, '')

      if (cleanInput === '') {
        this.emitValue(null)
        return
      }

      if (cleanInput.length !== 8) return

      const parsedDate = this.parseManualDate(inputValue)

      if (parsedDate && this.isDateValid(parsedDate)) {
        this.emitValue(parsedDate)
      }

      this.customizePickerButtons()
    },
    handleBlur() {
      let input = this.manualInputValue

      if (input instanceof Date) {
        if (this.isDateValid(input)) {
          this.emitValue(input)
        } else {
          this.emitValue(null)
        }
        return
      }

      const inputStr = String(input)
      const cleanInput = inputStr.replace(/\D/g, '')

      if (cleanInput.length !== 8) {
        this.emitValue(null)
        return
      }

      const parsedDate = this.parseManualDate(inputStr)

      if (!parsedDate || !this.isDateValid(parsedDate)) {
        this.emitValue(null)
      } else {
        this.emitValue(parsedDate)
      }
    },
    parseManualDate(input) {
      if (!input || typeof input !== 'string') return null

      const cleanInput = input.replace(/\D/g, '')

      if (cleanInput.length !== 8) {
        return null
      }

      const day = parseInt(cleanInput.substring(0, 2))
      const month = parseInt(cleanInput.substring(2, 4))
      const year = parseInt(cleanInput.substring(4, 8))

      if (month < 1 || month > 12) return null
      if (day < 1 || day > 31) return null

      const date = new Date(year, month - 1, day)

      if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
        return null
      }

      return date
    },

    isDateValid(date) {
      if (!date) return false

      return !(this.maxDate && date > this.maxDate)
    }
  },
  watch: {
    modelValue(newValue) {
      this.localModel = newValue
    }
  }
}
</script>

<template>
  <v-date-input
    v-model="localModel"
    :label="$t('COMMON.DATE_PLACEHOLDER')"
    :placeholder="$t('COMMON.DATE_PLACEHOLDER')"
    clearable
    hide-details
    @input="handleManualInput"
    @update:model-value="handleDateUpdate"
    @click:clear="handleClearValue"
    @blur="handleBlur"
    :max="maxDate"
  />
</template>
